import React, { useState } from 'react';
import { ErrorBadge } from './ErrorBadge';
import { sectionConfig } from '../utils/sectionConfig';

interface FormSection {
  code: string;
  name: string;
  category: string;
  data: any;
}

interface FormSectionTabsProps {
  sections: FormSection[];
  onSectionChange: (sectionCode: string) => void;
  getSectionErrorCount?: (sectionCode: string) => number;
  isCompactMode?: boolean; // New prop for PDF viewer mode
  activeSection?: string;  // Pass active section from parent
}

/**
 * FormSectionTabs - Mirrors your 1.0 form-tailwind.html sub-panel structure exactly
 * Provides categorized section navigation (Disclosure/Statements/Notes)
 */
export const FormSectionTabs: React.FC<FormSectionTabsProps> = React.memo(({ 
  sections, 
  onSectionChange,
  getSectionErrorCount,
  isCompactMode = false,
  activeSection: externalActiveSection
}) => {
  const [internalActiveSection, setInternalActiveSection] = useState<string>(sections[0]?.code || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [validationTrigger, setValidationTrigger] = useState(0);
  
  // Listen for validation updates (from useValidation DOM manipulation)
  React.useEffect(() => {
    const handler = () => {
      setValidationTrigger(prev => prev + 1); // Force re-render to update badges
    };
    document.addEventListener('validationUpdated', handler);
    return () => document.removeEventListener('validationUpdated', handler);
  }, []);
  
  // Listen for section changes from EditFilings (bus event - NO PROPS, NO PARENT RE-RENDERS)
  React.useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent;
      const sectionCode = customEvent.detail?.sectionCode;
      if (sectionCode) {
        setInternalActiveSection(sectionCode);
      }
    };
    document.addEventListener('sectionChanged', handler);
    return () => document.removeEventListener('sectionChanged', handler);
  }, []);
  
  // Use external active section if provided, otherwise use internal
  const activeSection = externalActiveSection || internalActiveSection;

  const handleTabClick = React.useCallback((sectionCode: string) => {
    // Always update internal state so dropdown text updates
    setInternalActiveSection(sectionCode);
    onSectionChange(sectionCode);
    setIsDropdownOpen(false); // Close dropdown after selection
  }, [onSectionChange]);

  // Group sections by category - matches your 1.0 logic
  const groupedSections: Record<string, FormSection[]> = {};
  const displayedCategories: string[] = [];

  sections.forEach(section => {
    const config = sectionConfig[section.code] || {
      display_name: section.name,
      category: 'Other'
    };
    
    if (!groupedSections[config.category]) {
      groupedSections[config.category] = [];
    }
    
    groupedSections[config.category].push({
      ...section,
      name: config.display_name
    });
  });

  // Get current active section details
  const currentSection = sections.find(s => s.code === activeSection);
  const currentSectionConfig = currentSection ? sectionConfig[currentSection.code] : null;
  const currentSectionName = currentSectionConfig?.display_name || currentSection?.name || 'Select Section';

  // Navigation handlers with proper dependencies
  const handlePrevious = React.useCallback(() => {
    // Get current active section (use external if provided)
    const current = externalActiveSection || internalActiveSection;
    const currentIndex = sections.findIndex(s => s.code === current);
    
    if (currentIndex > 0) {
      const prevSection = sections[currentIndex - 1];
      handleTabClick(prevSection.code);
    }
  }, [externalActiveSection, internalActiveSection, sections, handleTabClick]);
  
  const handleNext = React.useCallback(() => {
    // Get current active section (use external if provided)
    const current = externalActiveSection || internalActiveSection;
    const currentIndex = sections.findIndex(s => s.code === current);
    
    if (currentIndex >= 0 && currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      handleTabClick(nextSection.code);
    }
  }, [externalActiveSection, internalActiveSection, sections, handleTabClick]);
  
  // Find current section index and determine prev/next for rendering
  const currentSectionIndex = sections.findIndex(s => s.code === activeSection);
  const hasPrevious = currentSectionIndex > 0;
  const hasNext = currentSectionIndex < sections.length - 1;

  // Compact mode - dropdown with prev/next buttons
  if (isCompactMode) {
    return (
      <div className="form-section-dropdown">
        {/* Previous Button */}
        <button 
          type="button"
          className="section-nav-btn section-nav-prev"
          onClick={handlePrevious}
          disabled={!hasPrevious}
          title="Previous section"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>

        {/* Dropdown */}
        <button 
          type="button"
          className="section-dropdown-trigger"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <span className="section-dropdown-text">{currentSectionName}</span>
          {/* ✅ Group badge + arrow icon together on the right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Show error badge for current section (same as normal mode) */}
            {getSectionErrorCount && currentSection && (
              <ErrorBadge count={getSectionErrorCount(currentSection.code)} />
            )}
            <svg 
              className={`section-dropdown-icon ${isDropdownOpen ? 'rotated' : ''}`}
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <polyline points="6,9 12,15 18,9"></polyline>
            </svg>
          </div>
        </button>
        
        {/* Next Button */}
        <button 
          type="button"
          className="section-nav-btn section-nav-next"
          onClick={handleNext}
          disabled={!hasNext}
          title="Next section"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        </button>
        
        {isDropdownOpen && (
          <div className="section-dropdown-menu">
            {Object.entries(groupedSections).map(([category, categorySections]) => (
              <div key={category} className="section-dropdown-category">
                <div className="section-dropdown-category-header">{category}</div>
                {categorySections.map((section) => (
                  <button
                    key={section.code}
                    type="button"
                    className={`section-dropdown-item ${activeSection === section.code ? 'active' : ''}`}
                    onClick={() => handleTabClick(section.code)}
                  >
                    {section.name}
                    {getSectionErrorCount && (
                      <ErrorBadge count={getSectionErrorCount(section.code)} />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Normal mode - sidebar tabs
  return (
    <div className="sub-panel">
      <ul className="sub-panel-list">
        {Object.entries(groupedSections).map(([category, categorySections]) => (
          <React.Fragment key={category}>
            {/* Category Header - matches your 1.0 structure */}
            <li className="sub-panel-category">
              <h4><i>{category}</i></h4>
            </li>
            
            {/* Section Items */}
            {categorySections.map((section) => (
              <li 
                key={section.code}
                className={`sub-panel-item${activeSection === section.code ? ' sub-panel-active' : ''}`}
              >
                <a 
                  className="btn-sub-panel" 
                  data-tab={section.code}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTabClick(section.code);
                  }}
                >
                  {section.name}
                  {getSectionErrorCount && (
                    <ErrorBadge count={getSectionErrorCount(section.code)} />
                  )}
                </a>
              </li>
            ))}
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
});
